import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { CommunicationConfig, UnboaredGame, useListener } from '@unboared/react';
import { CouponPopup, useCouponPopup } from '~/components/coupon/CouponPopup';
import { CouponWonModal } from '~/components/coupon/CouponWonModal';
import { SessionClosedOverlay, GamepadLoader } from '~/components/session';
import { CODE_CLIENT_ID, CODE_LANG, CODE_SOCKET_SERVER } from '~/config/endpoints.config';

import { CLIENT_THEME_CSS, DEFAULT_SCENE, resolveSocketServer, socketServerToken } from '~/config/global.config';
import { useFullscreen } from '~/hooks/useFullscreen';
import { useHeadTags, HeadTag } from '~/hooks/useHeadTags';
import { useKeepAlive } from '~/hooks/useKeepAlive';
import { useBrandTheme, useClientBranding } from '~/hooks/useClientBranding';
import { PlayerData } from '~/types/player/player';

import { ProfileSetting } from '~/pages/ProfileSetting';
import { GamepadManager } from '~/pages/GamepadManager';
import { useLoadVenueLanguagePicker, useVenueLanguagePicker } from '~/stores/useVenueLanguagePicker';
import { readGamepadLang, readGamepadLangVenue } from '~/utils/gamepadLang';
import { honorsSavedLanguage, resolveGamepadLanguage } from '~/utils/playerLanguage';

function getGamepadLanguage(urlLang: string | null, honorSaved: boolean): string {
    return resolveGamepadLanguage({
        saved: readGamepadLang(),
        honorSaved,
        browser: navigator.language,
        url: urlLang,
    });
}

function GamepadCouponOverlay() {
    const { open } = useCouponPopup();
    useListener(
        '__couponWon__',
        (data: any) => {
            const { coupon } = data;
            open(<CouponWonModal coupon={coupon} />);
        },
        [open]
    );
    return null;
}

function GamepadAppThemed({
    lang,
    clientID,
    socketServer,
}: {
    lang: string | null;
    clientID: string | null;
    socketServer: string | null;
}) {
    const { i18n } = useTranslation();
    const [userData, setUserData] = useLocalStorage<any>('userData', {});
    const { sessionID } = useParams();

    // Auto-launch if we have saved session data (reconnection after page refresh)
    // Note: getSavedDeviceID is not yet implemented in @unboared/lib — disabled until the lib supports it
    const hasSavedSession = false;
    const [launched, setLaunched] = useState(hasSavedSession);
    const { isFullscreen, toggleFullscreen } = useFullscreen();

    // Option « chaque joueur choisit sa langue » du lieu (lue une fois) : décide si le
    // choix enregistré sur ce téléphone s'applique ici (cf. honorsSavedLanguage).
    useLoadVenueLanguagePicker(clientID);
    // Formule Pro : couleurs de l'établissement sur le téléphone (le logo : components/branding/BrandLogo).
    useBrandTheme(useClientBranding(clientID));
    const venueAllows = useVenueLanguagePicker((s) => s.venueAllows);
    const liveLang = getGamepadLanguage(lang, honorsSavedLanguage(clientID, venueAllows, readGamepadLangVenue()));
    // Figée au lancement : <UnboaredGame> ne lit `language` qu'au montage et le jeu
    // (iframe) la reçoit à ce moment-là ; une réponse tardive du réglage ne doit plus
    // séparer la console du jeu. En partie, seul le réglage du joueur la change
    // (GamepadSettings → API.loadLanguage, qui prévient les deux).
    const [launchLang, setLaunchLang] = useState<string | null>(null);
    const gamepadLang = launchLang ?? liveLang;

    useEffect(() => {
        i18n.changeLanguage(gamepadLang);
    }, [gamepadLang, i18n]);

    // Lock the parent page scroll while a game is live on the gamepad. The game
    // runs in an iframe and blocks touch scrolling internally, but that only
    // covers the iframe document — without this, drawing/dragging bounces THIS
    // console page on iOS (100vh > visual viewport) instead of reaching the game.
    // Scoped to launched only: the pre-launch ProfileSetting form relies on the
    // document scroll (min-height:100dvh container, no inner scroller) — with
    // data-collection fields on a small phone, locking there would put the join
    // CTA below the fold with no way to reach it. Class drives the CSS in
    // index.css (.gamepadScrollLock); admin/dashboard/screen pages keep their scroll.
    useEffect(() => {
        if (!launched) return undefined;
        const { documentElement: html, body } = document;
        html.classList.add('gamepadScrollLock');
        body.classList.add('gamepadScrollLock');
        return () => {
            html.classList.remove('gamepadScrollLock');
            body.classList.remove('gamepadScrollLock');
        };
    }, [launched]);

    const launchUI = async (newPlayer: PlayerData) => {
        setLaunchLang(liveLang);
        setLaunched(true);
        setUserData({ ...newPlayer });

        if (!isFullscreen) {
            toggleFullscreen();
        }
    };

    // Keep phone awake and handle reconnection when returning from background
    useKeepAlive();

    // Smart loader: swaps the spinner for a "session closed" screen when the
    // gamepad reloads onto a session the server has already killed (otherwise the
    // join fails silently and the spinner hangs forever).
    const loader = <GamepadLoader />;

    if (!launched) {
        return (
            <div className="gamepad">
                <ProfileSetting onSubmitted={launchUI} clientID={clientID || undefined} userData={userData} />
            </div>
        );
    }

    return (
        <div style={{ overflow: 'hidden' }}>
            <CouponPopup />
            <UnboaredGame
                initialScene={DEFAULT_SCENE}
                language={gamepadLang}
                loader={loader}
                config={CommunicationConfig.getSocketIO(
                    'gamepad',
                    // The screen propagates its socket server choice through
                    // the QR URL: screen and gamepads MUST join the same server
                    resolveSocketServer(socketServer),
                    {
                        id: sessionID,
                    },
                    {
                        uid: userData.uid,
                        username: userData.username,
                        avatar: userData.avatar,
                        color: userData.color,
                    },
                    // Only the Node.js server (v2) answers the resume protocol;
                    // on v1 (Python) it must stay disabled or the gamepad freezes
                    // on game change / reconnection (see lib supportsResume flag).
                    socketServerToken(socketServer) === 'v2'
                )}
            >
                <SessionClosedOverlay />
                <GamepadCouponOverlay />
                <GamepadManager />
            </UnboaredGame>
        </div>
    );
}

export default function GamepadApp() {
    const [searchParams] = useSearchParams();
    const lang = searchParams.get(CODE_LANG);
    const clientID = searchParams.get(CODE_CLIENT_ID);
    const socketServer = searchParams.get(CODE_SOCKET_SERVER);

    // Injecté après le CSS de l'app, donc gagne la cascade sur les `:root` de
    // index.css. Passait par <Helmet> jusqu'au 10/09/2026, donc ne partait pas
    // du tout (gotcha #154).
    const themeTags: HeadTag[] =
        clientID && CLIENT_THEME_CSS.includes(clientID)
            ? [{ tag: 'link', attrs: { rel: 'stylesheet', type: 'text/css', href: `/assets/css/${clientID}.css` } }]
            : [];
    useHeadTags(themeTags);

    return <GamepadAppThemed clientID={clientID} lang={lang} socketServer={socketServer} />;
}
